/* eslint-disable react/display-name, @typescript-eslint/no-empty-function */
/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: caoran
 * @LastEditTime: 2026-05-28 16:47:37
 */
import React, { useState } from 'react'
import { Layout, Menu, Modal } from 'antd'
import { Link, useLocation } from 'react-router-dom'
import { getBestMatchModulePath } from '../../utils/activeModules'
import sensors from '../../utils/sensors'
import { TodayIconFont as IconFont } from '../components/IconFonts'

import { setDownloadSettingModalVisible } from '../../actions/main'
import { useAppSelector, useAppDispatch } from '../../app/hooks'
import desktopLib from '../../utils/desktopLib'
import CONFIG from '../../constants/config'
import compareVersion from '../../utils/compareVersions'

import brand_banner_img from '../../images/brand.png'
import todaycam_logo_img from '../../images/todaycam_logo.png'

import CreateGroup from '../components/CreateGroup'
import JoinGroup from '../components/JoinGroup'
import {
  FileTextFilled,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingFilled
} from '@ant-design/icons'
import { isMingyangAiAllowed } from '../mingyangAi/constants'
import { shouldShowInspectionEntry } from '../myseInspect/utils/inspectionAccessConfig'
import AiClerkNewBadge, {
  markAiClerkBadgeSeen
} from '../aiClerk/components/AiClerkNewBadge'

// 菜单配置常量
const MENU_CONFIG = {
  photoManagement: {
    key: 'photo-management',
    title: '照片管理',
    icon: 'iconquanbuzhaopian',
    children: [
      { key: '/photo/all', title: '全部照片', path: '/photo/all' },
      { key: '/watermark/data', title: '照片台账表', path: '/watermark/data' },
      // AI 智能分类已全量，「分类文件夹」入口固定走新版 /ai-classify
      { key: '/ai-classify', title: '分类文件夹', path: '/ai-classify' },
      {
        key: '/ai/clerk',
        title: 'AI 填表员',
        path: '/ai/clerk',
        trackPath: '/ai-form-filler',
        trackExtra: {
          requirement_id: 1103,
          track_sign: 'aworkgroup.b8133.action.web_workgroup_nav_menu_cli'
        },
        // 「新」红橙小标签(Figma 1936:8157)；点击菜单后写 LS 并隐藏，下次不再显示。
        extraContent: <AiClerkNewBadge />,
        afterClick: markAiClerkBadgeSeen
      },
      { key: '/photo/location', title: '照片地点', path: '/photo/location' },
      { key: '/pathway', title: '拍照路线', path: '/pathway' },
      {
        key: '/ai/duplicate-photo',
        title: 'AI相似照片去重',
        path: '/ai/duplicate-photo',
        trackPath: 'AI检测重复照片'
      }
    ]
  },
  teamTools: {
    key: 'team-tools',
    title: '团队工具',
    icon: 'icontuanduigongju',
    children: [
      { key: '/watermark/list', title: '水印管理', path: '/watermark/list' },
      {
        key: '/verifyAuth/index',
        title: '照片验真',
        path: '/verifyAuth/index',
        trackPath: 'photoVerification'
      },
      {
        key: '/photo/jigsaw',
        title: '工作汇报',
        path: '/photo/jigsaw?from=menu'
      },
      { key: '/rectify/list', title: '整改记录', path: '/rectify/list' },
      {
        key: '/photo/audit',
        title: '开放平台',
        // 入口走外链(developer 站)；key 仅作菜单唯一标识，内部 /photo/audit 页面已下线删除
        external: true,
        href: 'https://developer.xhey.top/'
      },
      {
        // 任务表单：external 外链；非桌面客户端可见；href 根据 buildEnv 在
        // teamToolsConfig 里运行时注入
        key: 'form-space',
        title: '任务表单',
        external: true
      },
      {
        key: '/member/location',
        title: '人员实时定位',
        path: '/member/location',
        trackPath: 'deviceLocation'
      },
      {
        key: '/member/track',
        title: '实时轨迹',
        path: '/member/track',
        trackPath: 'realtimeTrack'
      },
      {
        key: '/credit',
        title: '工作信用查询',
        path: '/credit',
        trackPath: '信用分查询'
      }
    ]
  },
  teamStructure: {
    key: 'team-structure',
    title: '人员/权限',
    icon: 'icontuanduiguanli1',
    children: [
      {
        key: '/team/department',
        title: '成员/部门管理',
        path: '/team/department'
      },
      { key: '/team/admin', title: '管理员设置', path: '/team/admin' },
      { key: '/authority', title: '权限管理', path: '/authority' },
      { key: '/team/log', title: '操作日志', path: '/team/log' }
    ]
  },
  attendanceStats: {
    key: 'attendance-stats',
    title: '考勤统计',
    icon: 'iconkaoqin',
    children: [
      {
        key: '/attendance/daily',
        title: '每日统计',
        path: '/attendance/daily'
      },
      {
        key: '/attendance/period',
        title: '周月统计',
        path: '/attendance/period'
      },
      {
        key: '/attendance/ruleset',
        title: '考勤规则设置',
        path: '/attendance/ruleset'
      }
    ]
  },
  customerManagement: {
    key: 'customer-management',
    title: '客户管理',
    icon: 'iconkehubaifangtongji',
    children: [
      { key: '/visit/record', title: '拜访记录', path: '/visit/record' },
      { key: '/customer/info', title: '客户信息', path: '/customer/info' }
    ]
  }
}

// 其他常量
const FMCG_INDUSTRY_CODES = [
  '010030',
  '010020',
  '010040',
  '010050',
  '010060',
  '010010',
  '010070',
  '010080'
]

const CUSTOMER_TIP_INDUSTRY_CODES = [
  '010010',
  '010020',
  '010030',
  '010040',
  '010050',
  '010060',
  '010070'
]

const { Sider } = Layout
// antd 使用 items 属性，不再需要 SubMenu

const CustomersNewTip = (): React.ReactElement | null => {
  const isClicked =
    localStorage.getItem('today_camera_work_customer_clicked') === '1'
  const industryCode = useAppSelector((state) => state.group.industryCode)
  const myRole = useAppSelector((state) => state.group.myRole)

  // 对快消行业的管理员，有小红点提示，点击后消失
  if (
    !isClicked &&
    industryCode &&
    CUSTOMER_TIP_INDUSTRY_CODES.includes(industryCode) &&
    [1, 2].includes(myRole)
  ) {
    return <div className="new-feature-tip" />
  }
  return null
}

// antd 使用 items 模式，此函数返回 menu item 对象
const getClientSettingMenuItem = (onClick: () => void) => {
  if (
    desktopLib.isDesktop &&
    compareVersion(desktopLib.clientInfo.version, '1.2.0') >= 0
  ) {
    return {
      key: '/downloadSetting',
      label: (
        <>
          <SettingFilled />
          下载设置
        </>
      ),
      onClick
    }
  }
  return null
}

function SiderComponent() {
  const dispatch = useAppDispatch()
  const location = useLocation()

  // Redux 状态
  const activeModules = useAppSelector((state) => state.main.activeModules)
  const defaultModule = getBestMatchModulePath(
    activeModules || [],
    location.pathname
  )
  const group = useAppSelector((state) => state.group)
  const config = useAppSelector((state) => state.main.config || {})
  const isSpaceOpen = useAppSelector(
    (state: any) => state.mySpace?.personalSpace?.isSpaceOpen ?? false
  )

  // 本地状态
  const [joinGroupModuleVisible, setJoinGroupModuleVisible] = useState(false)
  const [createFromPlace, setCreateFromPlace] = useState('navMenu')
  const [joinFromPlace, setJoinFromPlace] = useState('navMenu')
  const [createGroupModuleVisible, setCreateGroupModuleVisible] =
    useState(false)
  // 侧边栏折叠：持久化 localStorage 刷新保留
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('side_collapsed') === '1'
    } catch {
      return false
    }
  })

  const handleToggleCollapse = () => {
    const next = !collapsed
    setCollapsed(next)
    try {
      localStorage.setItem('side_collapsed', next ? '1' : '0')
    } catch {
      // localStorage 不可用（无痕/被禁用）忽略
    }
    sensors.track('web_workgroup_nav_menu_cli', {
      path: next ? 'sideCollapse' : 'sideExpand',
      groupID: group.groupId,
      isGroupVIP: group.isVip,
      bool1: isSpaceOpen
    })
  }

  const hasGroup = !!group.groupId
  const clickDataStatistics =
    localStorage.getItem('clickDataStatistics') === 'true'
  const industryCode = group.industryCode
  const isFMCGIndustry = industryCode
    ? FMCG_INDUSTRY_CODES.includes(industryCode)
    : false // 快消行业
  const customerConfig = group.customerConfig // 获取客户配置
  const webCreditSwitch =
    config.web_credit_switch === 'true' || config.web_credit_switch === true

  const handleDownloadDesktop = () => {
    sensors.track('web_workgroup_nav_menu_cli', {
      path: 'downloadClientTop',
      groupID: group.groupId,
      isGroupVIP: group.isVip,
      bool1: isSpaceOpen
    })
    window.location.href = CONFIG.DESKTOP_DOWNLOAD_URL
  }

  const handleOpenDownloadSetting = () => {
    dispatch(setDownloadSettingModalVisible(true))
  }

  const handleCreateGroupe = (path: string) => {
    sensors.track('web_workgroup_nav_menu_cli', {
      path: path,
      groupID: group.groupId,
      isGroupVIP: group.isVip,
      isAvailable: false,
      bool1: isSpaceOpen
    })
    setCreateGroupModuleVisible(true)
  }

  // 通用的菜单项点击处理函数
  const handleMenuClick = (path: string, trackPath: string | null = null) => {
    sensors.track('web_workgroup_nav_menu_cli', {
      path: trackPath || path,
      groupID: group.groupId,
      isGroupVIP: group.isVip,
      bool1: isSpaceOpen
    })
    // 点击客户管理子菜单时消除红点
    if (path === '/visit/record' || path === '/customer/info') {
      localStorage.setItem('today_camera_work_customer_clicked', '1')
    }
  }

  // 获取动态菜单名
  const getDynamicTitle = (path: string, defaultTitle: string) => {
    if (path === '/visit/record' && customerConfig?.verb) {
      return `${customerConfig.verb}记录`
    } else if (path === '/customer/info' && customerConfig?.noun) {
      return `${customerConfig.noun}信息`
    }
    return defaultTitle
  }

  // antd 使用 items 属性，转换菜单项为对象
  const getMenuItem = (item: any) => {
    const dynamicTitle = getDynamicTitle(item.path, item.title)
    if (item.external) {
      // 桌面客户端(Electron)里 <a target="_blank"> 可能开内嵌窗口或不工作，
      // 用 todayCamera 桥(desktopLib.openContact)调系统默认浏览器，跟项目里
      // 其他外链入口(BuildingSafetyEntry 等)同款处理
      const handleExternalClick = (e: React.MouseEvent) => {
        if (desktopLib.isDesktop && item.href) {
          e.preventDefault()
          desktopLib.openContact(item.href)
        }
        handleMenuClick(item.key, item.trackPath)
      }
      return {
        key: item.key,
        label: (
          <a
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleExternalClick}
            style={item.style}
          >
            {(item.iconComponent || item.icon) &&
              (item.iconComponent || <IconFont type={item.icon} />)}
            {dynamicTitle}
            {item.extraContent}
          </a>
        )
      }
    }
    return {
      key: item.key,
      label: (
        <Link
          to={item.path}
          onClick={() => {
            if (item.onClick) {
              item.onClick()
            } else {
              handleMenuClick(item.path, item.trackPath)
            }
            // afterClick：菜单项可挂的"点完之后"hook，
            // 用途：AI 填表员「新」标签点击后写 LS 标记已读 → 下次不再显示。
            if (item.afterClick) item.afterClick()
          }}
          style={item.style}
        >
          {(item.iconComponent || item.icon) &&
            (item.iconComponent || <IconFont type={item.icon} />)}
          {dynamicTitle}
          {item.extraContent}
        </Link>
      )
    }
  }

  // antd 使用 items 属性，转换子菜单为对象
  const getSubMenu = (config: any) => {
    const dynamicTitle = getDynamicTitle(config.key, config.title)
    return {
      key: config.key,
      label: (
        <span onClick={() => handleMenuClick(config.key, config.trackPath)}>
          <IconFont type={config.icon} />
          {dynamicTitle}
          {config.extraContent}
        </span>
      ),
      children: config.children.map((item) => getMenuItem(item))
    }
  }

  // 数据统计菜单项
  const dataStatisticsItem = {
    key: '/data/statistics',
    path: '/data/statistics',
    title: '数据统计',
    icon: 'iconshujukanban',
    style: { fontWeight: 600 },
    extraContent: !clickDataStatistics && <span className="red-dot"></span>,
    onClick: () => {
      localStorage.setItem('clickDataStatistics', 'true')
      handleMenuClick('/data/statistics')
    }
  }

  const aiPhotoTagsState = group.aiPhotoTags
  const aiPhotoTagsGroupId = aiPhotoTagsState?.groupId ?? null
  const aiPhotoTagsCount = aiPhotoTagsState?.count ?? 0
  const aiPhotoTagsLoaded = aiPhotoTagsState?.loaded ?? false
  const aiPhotoPath =
    aiPhotoTagsLoaded &&
    aiPhotoTagsGroupId === group.groupId &&
    aiPhotoTagsCount > 0
      ? '/photo/ai-tags'
      : '/photo/ai'

  // 照片管理配置
  const photoManagementConfig = {
    ...MENU_CONFIG.photoManagement,
    children: [
      // AI 填表员入口已全量放开（追平 #726，原 KV/abtest 灰度门禁已下线）
      ...MENU_CONFIG.photoManagement.children,
      // 如果启用了AI智能看图，添加到子菜单中
      ...(group.show_ai_photo
        ? [
            {
              key: '/photo/ai',
              title: 'AI 智能看图',
              path: aiPhotoPath
            }
          ]
        : [])
    ]
  }

  // 客户管理配置
  const customerManagementConfig = {
    ...MENU_CONFIG.customerManagement,
    title: getDynamicTitle('/customer', MENU_CONFIG.customerManagement.title),
    extraContent: <CustomersNewTip />
  }

  // 当前用户角色与权限判断（用于菜单项可见性控制）
  const myRole = group.myRole
  const isAdmin =
    myRole === CONFIG.memberRoleTypes.ADMIN ||
    myRole === CONFIG.memberRoleTypes.MASTER_ADMIN

  // 团队工具（应用人员实时定位和工作信用的可见性过滤）
  // 权限控制说明：
  // 1. 人员实时定位：需要团队启用该功能且用户是管理员才显示
  // 2. 工作信用：需要 web_credit_switch 配置为 true 且用户是管理员才显示
  // 任务表单外链 URL 按环境切换：staging/prod 走正式域，test 走测试域
  const buildEnv = __BUILD_ENV__
  const formSpaceUrl =
    buildEnv === 'staging' || buildEnv === 'prod'
      ? 'https://photoform.5181688.com/?source=web'
      : 'https://test-photoform.5181688.com/login?redirect=%2Fforms'

  const teamToolsConfig = {
    ...MENU_CONFIG.teamTools,
    children: [
      ...MENU_CONFIG.teamTools.children
        .filter((item) => {
          // 人员实时定位权限检查
          if (item.key === '/member/location') {
            return group.show_member_location && isAdmin
          }
          // 实时轨迹：当前仅按管理员控制；后端实时轨迹开关字段就绪后，
          // 在此补上与 show_member_location 同类的团队配置判断
          if (item.key === '/member/track') {
            return isAdmin
          }
          // 工作信用权限检查：需要配置开启且是管理员
          if (item.key === '/credit') {
            return webCreditSwitch && isAdmin
          }
          // 任务表单：Electron 桌面客户端不显示；后端关闭 taskForm 时也隐藏
          if (item.key === 'form-space') {
            return !desktopLib.isDesktop && !group.taskForm?.enabled
          }
          // 其他菜单项正常显示
          return true
        })
        .map((item) => {
          if (item.key === 'form-space') {
            return { ...item, href: formSpaceUrl }
          }
          return item
        })
    ]
  }

  // antd 使用 items 属性构建菜单
  const menuItems = [
    // 数据统计
    getMenuItem(dataStatisticsItem),
    // AI 分析（演示项目）—— 仅 mingyangAi/constants.ts ALLOWED_GROUP_IDS 白名单团队可见。
    // 与基准 origin/master 一致：紧跟数据统计之后、照片管理之前，避免长菜单时落到折叠线以下。
    ...(isMingyangAiAllowed(group.groupId)
      ? [
          getMenuItem({
            key: '/mingyang-ai/analysis',
            path: '/mingyang-ai/analysis',
            title: 'AI 分析',
            // 与 #697 一致：用 antd 实心 file-text 图标（IconFont 无此字形会空白）。
            iconComponent: <FileTextFilled />,
            style: { fontWeight: 600 }
          })
        ]
      : []),
    // 检查项管理 —— 由远程 KV(workspace_inspection_config) 完全控制入口可见性，
    // 后台修改即时生效，前端不留 hardcode 白名单；历史「点位管理」由本菜单完整
    // 替代，旧 myseOrders 模块已下线。与基准一致：紧跟 AI 分析之后、照片管理之前。
    ...(shouldShowInspectionEntry(
      (config as any)['workspace_inspection_config'],
      group.invite_id,
      group.groupId
    )
      ? [
          getSubMenu({
            key: 'myse-inspect',
            title: '检查项管理',
            icon: 'icontuanduigongju',
            children: [
              {
                key: '/myse/check-records',
                title: '检查项管理',
                path: '/myse/check-records'
              },
              {
                key: '/myse/projects',
                title: '检查项配置',
                path: '/myse/projects'
              },
              {
                key: '/myse/knowledge-base',
                title: '知识库管理',
                path: '/myse/knowledge-base'
              },
              {
                key: '/myse/downloads',
                title: '下载管理',
                path: '/myse/downloads'
              }
            ]
          })
        ]
      : []),
    // 照片管理
    getSubMenu(photoManagementConfig),
    // AI安全隐患台账表
    getMenuItem({
      key: '/ai/security',
      path: '/ai/security',
      title: 'AI安全隐患台账表',
      icon: 'iconexcel',
      style: { fontWeight: 600 },
      trackPath: 'aiSecurity'
    }),
    // 客户管理 - 快消行业时放在照片管理下方
    ...(isFMCGIndustry ? [getSubMenu(customerManagementConfig)] : []),
    // 团队工具
    getSubMenu(teamToolsConfig),
    // 人员/权限
    getSubMenu(MENU_CONFIG.teamStructure),
    // 考勤统计
    getSubMenu(MENU_CONFIG.attendanceStats),
    // 客户管理 - 非快消行业时放在原位置
    ...(!isFMCGIndustry ? [getSubMenu(customerManagementConfig)] : []),
    // 分隔线：antd 使用 type: 'divider'
    {
      type: 'divider',
      key: 'divider',
      style: {
        margin: '0px 0 0px 20px',
        backgroundColor: '#DCDEE3'
      }
    },
    // 使用手册
    getMenuItem({
      key: '/userManual',
      path: '/userManual',
      title: '使用手册',
      icon: defaultModule.includes('/userManual')
        ? 'iconcaozuoshouce'
        : 'iconmanual1'
    }),
    // 联系客服
    getMenuItem({
      key: '/qa',
      path: '/qa',
      title: '联系客服',
      icon: defaultModule.includes('/qa')
        ? 'iconlianxikefu21'
        : 'iconlianxikefu'
    }),
    // 下载设置
    getClientSettingMenuItem(handleOpenDownloadSetting)
  ].filter(Boolean)

  // 无团队时的照片管理子菜单
  const noGroupPhotoManagement = {
    key: 'photo-management',
    title: '照片管理',
    icon: 'iconquanbuzhaopian',
    children: [
      { key: '/photo/all', title: '全部照片' },
      { key: '/watermark/data', title: '照片台账表' },
      { key: '/ai-classify', title: '分类文件夹' },
      { key: '/photo/location', title: '照片地点' },
      { key: '/pathway', title: '拍照路线' },
      { key: '/photo/ai', title: 'AI 智能看图' }
    ]
  }

  const noGroupSubMenus = [
    noGroupPhotoManagement,
    {
      key: '/customer',
      title: getDynamicTitle('/customer', '客户管理'),
      icon: 'iconkehubaifangtongji',
      children: [
        {
          key: '/visit/record',
          title: getDynamicTitle('/visit/record', '拜访记录')
        },
        {
          key: '/customer/info',
          title: getDynamicTitle('/customer/info', '客户信息')
        }
      ]
    },
    {
      key: '/team',
      title: '团队管理',
      icon: 'icontuanduiguanli1',
      children: [
        { key: '/team/department', title: '成员/部门管理' },
        { key: '/team/admin', title: '管理员设置' },
        { key: '/team/log', title: '操作日志' },
        { key: '/photo/jigsaw', title: '工作汇报' }
      ]
    },
    {
      key: '/attendance',
      title: '考勤统计',
      icon: 'iconkaoqin',
      children: [
        { key: '/attendance/daily', title: '每日统计' },
        { key: '/attendance/period', title: '周月统计' },
        { key: '/attendance/ruleset', title: '考勤规则设置' }
      ]
    }
  ]

  const noGroupBottomItems = [
    { key: '/watermark/list', title: '水印管理', icon: 'iconshuiyinguanli1' },
    { key: '/authority', title: '权限管理', icon: 'iconquanxianguanli' },
    { key: '/userManual', title: '使用手册', icon: 'iconmanual1' },
    { key: '/qa', title: '联系客服', icon: 'iconlianxikefu' }
  ]

  // antd 使用 items 属性，转换无团队时的菜单项为对象
  const getNoGroupMenuItem = (item: any) => ({
    key: item.key,
    disabled: true,
    label: (
      <div onClick={() => handleCreateGroupe(item.key)}>
        {(item.iconComponent || item.icon) &&
          (item.iconComponent || <IconFont type={item.icon} />)}
        {getDynamicTitle(item.key, item.title)}
      </div>
    )
  })

  const getNoGroupSubMenu = (menuConfig: any) => ({
    key: menuConfig.key,
    disabled: true,
    label: (
      <span onClick={() => handleCreateGroupe(menuConfig.key)}>
        <IconFont type={menuConfig.icon} />
        {getDynamicTitle(menuConfig.key, menuConfig.title)}
      </span>
    ),
    children: menuConfig.children.map((item) => ({
      key: item.key,
      disabled: true,
      label: (
        <div onClick={() => handleCreateGroupe(item.key)}>
          {getDynamicTitle(item.key, item.title)}
        </div>
      )
    }))
  })

  // 无团队时的菜单项数组
  const noGroupMenuItems = [
    ...noGroupSubMenus.map(getNoGroupSubMenu),
    ...noGroupBottomItems.map(getNoGroupMenuItem)
  ] as any

  // 折叠/展开 toggle 行：fixed 压在 brand banner 上方，跟普通菜单项视觉一致
  const collapseToggle = (
    <div
      className={'sider-collapse-fixed' + (collapsed ? ' is-collapsed' : '')}
      onClick={handleToggleCollapse}
      role="button"
      tabIndex={0}
      title={collapsed ? '展开导航' : '收起导航'}
      aria-label={collapsed ? '展开导航' : '收起导航'}
    >
      {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
      <span>收起导航</span>
    </div>
  )

  return (
    <div>
      {hasGroup ? (
        <Sider
          className={'sider' + (collapsed ? ' is-collapsed' : '')}
          width={190}
          collapsed={collapsed}
          collapsedWidth={64}
          trigger={null}
        >
          {/* antd 使用 items 属性 */}
          <Menu
            mode="inline"
            inlineCollapsed={collapsed}
            selectedKeys={
              Array.isArray(defaultModule) ? defaultModule : [defaultModule]
            }
            defaultOpenKeys={
              collapsed
                ? []
                : isFMCGIndustry
                  ? ['photo-management', 'team-tools', 'customer-management']
                  : ['photo-management', 'team-tools']
            }
            className="short-menu"
            items={menuItems as any}
          />

          {collapseToggle}
          <div
            className={
              'brand-banner-fixed' + (collapsed ? ' is-collapsed' : '')
            }
          >
            <img
              src={collapsed ? todaycam_logo_img : brand_banner_img}
              alt=""
            />
          </div>
        </Sider>
      ) : (
        <Sider
          className={'sider' + (collapsed ? ' is-collapsed' : '')}
          width={190}
          collapsed={collapsed}
          collapsedWidth={64}
          trigger={null}
        >
          {/* 登录无团队可以进入web验真页，点击其他区域创建团队 */}
          <Menu
            mode="inline"
            inlineCollapsed={collapsed}
            selectedKeys={['/verifyAuth/index']}
            className="short-menu"
            items={[
              {
                key: '/verifyAuth/index',
                label: (
                  <Link
                    to="/verifyAuth/index"
                    onClick={() => {
                      sensors.track('web_workgroup_nav_menu_cli', {
                        path: 'photoVerification',
                        groupID: group.groupId,
                        isGroupVIP: group.isVip,
                        bool1: isSpaceOpen
                      })
                    }}
                  >
                    <IconFont type="iconyan" />
                    照片验真
                  </Link>
                )
              }
            ]}
          />
          <div>
            <Menu
              mode="inline"
              inlineCollapsed={collapsed}
              defaultOpenKeys={
                collapsed
                  ? []
                  : ['photo-management', '/customer', '/team', '/attendance']
              }
              items={noGroupMenuItems}
            />
            {collapseToggle}
            <div
              className={
                'brand-banner-fixed' + (collapsed ? ' is-collapsed' : '')
              }
            >
              <img
                src={collapsed ? todaycam_logo_img : brand_banner_img}
                alt=""
              />
            </div>
          </div>
        </Sider>
      )}
      <Modal
        open={createGroupModuleVisible}
        footer={null}
        closable={true}
        mask={{ closable: true }}
        width={394}
        style={{ top: '20%' }}
        onCancel={() => setCreateGroupModuleVisible(false)}
      >
        <CreateGroup
          fromPlace={createFromPlace}
          onGotoJoin={() => {
            setCreateGroupModuleVisible(false)
            setJoinFromPlace('createGroup')
            setJoinGroupModuleVisible(true)
          }}
        />
      </Modal>
      <Modal
        open={joinGroupModuleVisible}
        footer={null}
        closable={true}
        mask={{ closable: true }}
        width={394}
        className="join-group-modal"
        style={{ top: '20%' }}
        styles={{ body: { padding: 0, height: 516 } }}
        onCancel={() => setJoinGroupModuleVisible(false)}
        destroyOnHidden
      >
        <JoinGroup
          fromPlace={joinFromPlace}
          onClose={() => setJoinGroupModuleVisible(false)}
          onGotoCreate={() => {
            setJoinGroupModuleVisible(false)
            setCreateFromPlace('joinGroup')
            setCreateGroupModuleVisible(true)
          }}
        />
      </Modal>
    </div>
  )
}

export default SiderComponent
